import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getClientPublicEntryPath } from '@/config/tenant'
import { useUser } from '@/contexts/UserContext'
import { useToast } from '@/contexts/ToastContext'
import { updatePerfil, uploadImage, deleteMyAccount } from '@/api/usuariosApi'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import { isAdmin } from '@/utils/adminAuth'
import {
  COUNTRY_OPTIONS,
  type CountryCode,
  buildPhoneForStorage,
  detectCountryFromPhone,
  formatPhone,
} from '@/utils/phone'
import { TenantIdentityCard } from '@/components/TenantIdentity'
import { GuestClientPanel } from '@/components/GuestClientPanel'
import { ClientPageLayout, clientPageStyles as layout } from '@/components/ClientPageLayout'
import { isGuestClient } from '@/utils/guestAuth'
import styles from './PerfilPage.module.css'

export function PerfilPage() {
  const navigate = useNavigate()
  const { user, setUser, logout } = useUser()
  const { toast } = useToast()
  const [editando, setEditando] = useState(false)
  const [nome, setNome] = useState(user?.nome ?? '')
  const initialPais = detectCountryFromPhone(user?.telefone ?? '')
  const [telefone, setTelefone] = useState(formatPhone(user?.telefone ?? '', initialPais))
  const [telefonePais, setTelefonePais] = useState<CountryCode>(
    detectCountryFromPhone(user?.telefone ?? ''),
  )
  const [salvando, setSalvando] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  if (!user) {
    if (isAdmin()) {
      return (
        <ClientPageLayout>
          <div className={layout.card}>
            <p className={layout.lead}>
              Pré-visualização como administrador: inicie sessão como cliente para ver e editar o
              perfil.
            </p>
            <button
              type="button"
              className={layout.btnPrimary}
              onClick={() => navigate('/admin/dashboard')}
            >
              Voltar ao painel admin
            </button>
          </div>
        </ClientPageLayout>
      )
    }
    if (isGuestClient(user)) {
      return (
        <ClientPageLayout>
          <TenantIdentityCard compact className={styles.empresaCard} />
          <GuestClientPanel variant="perfil" />
        </ClientPageLayout>
      )
    }
    return null
  }

  const handleAbrirEdicao = () => {
    setNome(user.nome)
    const nextPais = detectCountryFromPhone(user.telefone ?? '')
    setTelefonePais(nextPais)
    setTelefone(formatPhone(user.telefone ?? '', nextPais))
    setEditando(true)
  }

  const handleCancelarEdicao = () => {
    setNome(user.nome)
    const nextPais = detectCountryFromPhone(user.telefone ?? '')
    setTelefonePais(nextPais)
    setTelefone(formatPhone(user.telefone ?? '', nextPais))
    setEditando(false)
  }

  const handleSalvarPerfil = async (e: React.FormEvent) => {
    e.preventDefault()
    setSalvando(true)
    try {
      const atualizado = await updatePerfil(user.id, {
        nome: nome.trim(),
        telefone: telefone.trim() ? buildPhoneForStorage(telefone, telefonePais) : null,
      })
      setUser({ ...user, ...atualizado })
      setEditando(false)
      toast('Perfil atualizado com sucesso.', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao guardar.', 'error')
    } finally {
      setSalvando(false)
    }
  }

  const handleTelefoneChange = (value: string) => {
    const formatted = value ? formatPhone(value, telefonePais) : ''
    setTelefone(formatted)
  }

  const handleTelefonePaisChange = (code: CountryCode) => {
    setTelefonePais(code)
    if (telefone.trim()) {
      setTelefone(formatPhone(telefone, code))
    }
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !file.type.startsWith('image/')) {
      toast('Selecione uma imagem válida.', 'error')
      return
    }
    setUploadingAvatar(true)
    try {
      const url = await uploadImage(file)
      await updatePerfil(user.id, { avatar_url: url })
      setUser({ ...user, avatar_url: resolveMediaUrl(url) })
      toast('Foto atualizada.', 'success')
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erro ao enviar foto.', 'error')
    } finally {
      setUploadingAvatar(false)
      e.target.value = ''
    }
  }

  const handleSair = () => {
    logout()
    toast('Sessão encerrada.', 'info')
    window.location.replace(getClientPublicEntryPath())
  }

  const handleEliminarConta = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }
    setDeleting(true)
    try {
      await deleteMyAccount()
      logout()
      toast('Conta eliminada permanentemente.', 'success')
      window.location.replace(getClientPublicEntryPath())
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Não foi possível eliminar a conta.', 'error')
      setDeleting(false)
      setConfirmDelete(false)
    }
  }

  return (
    <ClientPageLayout>
      <TenantIdentityCard compact className={styles.empresaCard} />
      <div className={`${layout.card} ${styles.profileCard}`}>
        <div className={styles.avatarWrap}>
          <div className={styles.avatar}>
            {user.avatar_url ? (
              <img src={user.avatar_url} alt="" />
            ) : (
              <span>{user.nome.charAt(0).toUpperCase()}</span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className={styles.avatarInput}
            onChange={handleAvatarChange}
            disabled={uploadingAvatar}
            aria-label="Alterar foto de perfil"
          />
          <button
            type="button"
            className={styles.avatarBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingAvatar}
          >
            {uploadingAvatar ? 'A enviar…' : 'Alterar foto'}
          </button>
        </div>

        {!editando ? (
          <div className={styles.infoBlock}>
            <p className={styles.userName}>{user.nome}</p>
            {user.email && <p className={styles.userEmail}>{user.email}</p>}
            {user.telefone && <p className={styles.userMeta}>{user.telefone}</p>}
            <button type="button" className={styles.btnEditar} onClick={handleAbrirEdicao}>
              Editar perfil
            </button>
          </div>
        ) : (
          <form className={styles.form} onSubmit={handleSalvarPerfil}>
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="perfil-nome">
                Nome
              </label>
              <input
                id="perfil-nome"
                type="text"
                className={styles.input}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                autoComplete="name"
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor="perfil-telefone">
                Telefone
              </label>
              <div className={styles.phoneRow}>
                <select
                  className={styles.phoneCountry}
                  value={telefonePais}
                  onChange={(e) => handleTelefonePaisChange(e.target.value as CountryCode)}
                  aria-label="Código do país"
                >
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {c.dial}
                    </option>
                  ))}
                </select>
                <input
                  id="perfil-telefone"
                  type="tel"
                  className={`${styles.input} ${styles.phoneInput}`}
                  value={telefone}
                  onChange={(e) => handleTelefoneChange(e.target.value)}
                  placeholder={
                    telefonePais === 'PT'
                      ? '912 345 678 (opcional)'
                      : '(11) 91234-5678 (opcional)'
                  }
                  autoComplete="tel"
                />
              </div>
            </div>
            <div className={styles.formActions}>
              <button type="button" className={styles.btnSec} onClick={handleCancelarEdicao}>
                Cancelar
              </button>
              <button type="submit" className={styles.btnPrim} disabled={salvando}>
                {salvando ? 'A guardar…' : 'Guardar'}
              </button>
            </div>
          </form>
        )}

        <button type="button" className={styles.btnSair} onClick={handleSair}>
          Sair da conta
        </button>

        <div className={styles.deleteBlock}>
          {!confirmDelete ? (
            <button type="button" className={styles.btnDelete} onClick={handleEliminarConta}>
              Eliminar conta
            </button>
          ) : (
            <>
              <p className={styles.deleteWarn} role="alert">
                Isto apaga permanentemente a sua conta, marcações e dados associados. Não é possível
                reverter.
              </p>
              <div className={styles.deleteActions}>
                <button
                  type="button"
                  className={styles.btnSec}
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className={styles.btnDeleteConfirm}
                  onClick={handleEliminarConta}
                  disabled={deleting}
                >
                  {deleting ? 'A eliminar…' : 'Confirmar eliminação'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </ClientPageLayout>
  )
}
